import React, { useMemo } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { Navigation } from './components/layout/Navigation';
import { Dashboard } from './pages/Dashboard';
import { AssetsList } from './pages/AssetsList';
import { AssetDetail } from './pages/AssetDetail';
import { Login } from './pages/Login';
import { IdentificationRules } from './pages/IdentificationRules';
import { config, isAuthConfigured } from './config';
import { setAuthToken } from './services/apiClient';
import { resources } from './resources';

/** Build OIDC config at runtime so it uses loaded config (from /config.json), not module-load defaults. */
function getOidcConfig() {
  return {
    authority: config.auth.authority,
    client_id: config.auth.clientId,
    redirect_uri: config.auth.redirectUri,
    post_logout_redirect_uri: config.auth.postLogoutRedirectUri,
    scope: config.auth.scopes,
    response_type: 'code',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  };
}

/** Shown when /config.json has not provided OIDC (e.g. local dev without auth). */
const AuthNotConfigured: React.FC = () => (
  <div className="min-h-screen bg-background flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-card p-10 rounded-3xl border border-border shadow-xl text-center text-muted-foreground">
      <p className="font-medium">{resources.login.title}</p>
      <p className="mt-2 text-sm">Authentication is not configured for this environment. Provide OIDC settings in runtime config (e.g. /config.json) to sign in.</p>
    </div>
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const location = useLocation();

  // Set token synchronously during render so the first data fetches in child
  // components (Dashboard, AssetsList, etc.) see it. If we only set in useEffect,
  // those useEffects run in the same tick and getAuthToken() is still undefined.
  setAuthToken(auth.user?.access_token);

  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-primary rounded-xl"></div>
          <div className="h-4 w-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Navigation>{children}</Navigation>;
};

const AppRoutes: React.FC = () => {
  const auth = useAuth();

  return (
    <Routes>
      <Route path="/login" element={auth.isAuthenticated ? <Navigate to="/" replace /> : <Login />} />

      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/assets" element={<ProtectedRoute><AssetsList /></ProtectedRoute>} />
      <Route path="/assets/:assetId" element={<ProtectedRoute><AssetDetail /></ProtectedRoute>} />

      <Route path="/identification-rules" element={<ProtectedRoute><IdentificationRules /></ProtectedRoute>} />

      <Route path="/settings" element={
        <ProtectedRoute>
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <div className="bg-card p-8 rounded-3xl border border-border text-center space-y-4">
               <h2 className="text-xl font-bold text-foreground">{resources.settings.title}</h2>
               <p>{resources.settings.placeholder}</p>
            </div>
          </div>
        </ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  const oidcConfig = useMemo(() => getOidcConfig(), []);
  if (!isAuthConfigured()) {
    return <AuthNotConfigured />;
  }
  return (
    <AuthProvider {...oidcConfig}>
      <HashRouter>
        <AppRoutes />
      </HashRouter>
    </AuthProvider>
  );
};

export default App;
